import { useTranslation } from "react-i18next"

import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/shadcn/components/ui/dialog"
import { cn } from "@/shadcn/lib/utils"
import { useDialogCloseHint } from "./useDialogCloseHint"

export type DialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  persistent?: boolean
  contentClassName?: string
  bodyClassName?: string
  renderBody: () => React.ReactNode
  renderFooter?: () => React.ReactNode
}

export default function Dialog(props: DialogProps) {
  const { t } = useTranslation("common")
  const { contentRef, isCloseHintOpen, setIsCloseHintOpen, triggerHint } = useDialogCloseHint()

  const refuseDismissIfPersistent = (event: Event) => {
    if (!props.persistent) {
      return
    }

    event.preventDefault()
    triggerHint()
  }

  return (
    <DialogRoot open={props.isOpen} onOpenChange={props.onOpenChange}>
      <DialogContent
        ref={contentRef}
        className={cn("flex max-h-[90vh] flex-col overflow-hidden sm:max-w-sm", props.contentClassName)}
        onEscapeKeyDown={refuseDismissIfPersistent}
        onPointerDownOutside={refuseDismissIfPersistent}
        // Controlled rather than force-open, so hover keeps working on every dialog and a
        // persistent one can additionally pop it open on a refused dismiss.
        closeButtonTooltip={{
          content: t("dialogs.close"),
          open: isCloseHintOpen,
          onOpenChange: setIsCloseHintOpen,
        }}
      >
        <DialogHeader>
          <DialogTitle>{props.title}</DialogTitle>
          {props.description && <DialogDescription>{props.description}</DialogDescription>}
        </DialogHeader>

        <div className={cn("min-h-0 overflow-y-auto", props.bodyClassName)}>{props.renderBody()}</div>

        {props.renderFooter && <DialogFooter>{props.renderFooter()}</DialogFooter>}
      </DialogContent>
    </DialogRoot>
  )
}
